
import React, { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MoreVertical, Eye, EyeOff, ArchiveIcon, ArchiveRestore, Trash2 } from "lucide-react";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { CompactStatusBadge } from "@/components/notifications/StatusBadges";
import ChatStatusIndicator from "@/components/notifications/ChatStatusIndicator";
import { cn } from "@/lib/utils";
import { useNotificationReadStatus } from "@/hooks/useNotificationReadStatus";
import { SupabaseNotification } from "@/types/notifications";
import StatusChangeDropdown from "../list/StatusChangeDropdown";
import { StockStatusBadge } from "../StockStatusBadge";
import { isAwaitingStockStatus } from "@/utils/statusMappings";
import { StockAlertDialog } from "../StockAlertDialog";
import { formatDistanceToNow } from "date-fns";
import { nl } from "date-fns/locale";

interface MobileNotificationCardProps {
  notification: SupabaseNotification;
  formatDate: (date: string) => string;
  onNotificationClick: (id: string) => void;
  onArchive: (e: React.MouseEvent, id: string) => void;
  onRestore: (e: React.MouseEvent, id: string) => void;
  onDelete: (e: React.MouseEvent, id: string) => void;
  selectedStatuses: string[];
  viewMode: "active" | "archived";
  unreadChatCount?: number;
}

const MobileNotificationCard = ({
  notification,
  formatDate,
  onNotificationClick,
  onArchive,
  onRestore,
  onDelete,
  selectedStatuses,
  viewMode,
  unreadChatCount = 0
}: MobileNotificationCardProps) => {
  const { markAsRead, markAsUnread, isNotificationRead } = useNotificationReadStatus();
  const isRead = isNotificationRead(notification.id);
  const isHighPriority = notification.priority === 'hoog';
  const [showStockDialog, setShowStockDialog] = useState(false);
  const voNumbers = notification.vo_number?.split(",") || [];
  const isBulk = voNumbers.length > 1;
  
  const handleMarkAsRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    markAsRead.mutate(notification.id);
  };

  const handleMarkAsUnread = (e: React.MouseEvent) => {
    e.stopPropagation();
    markAsUnread.mutate(notification.id);
  };

  const handleStatusClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const relativeTime = formatDistanceToNow(new Date(notification.created_at), { 
    addSuffix: false, 
    locale: nl 
  });

  return (
    <div 
      className={cn(
        "relative px-3 py-2.5 cursor-pointer active:bg-muted/50 transition-colors",
        !isRead && "bg-primary/5",
        isHighPriority && "border-l-[3px] border-l-destructive"
      )}
      onClick={() => onNotificationClick(notification.id)}
    >
      {/* Row 1: VO + Status + Time + Menu */}
      <div className="flex items-center gap-2 min-h-[24px]">
        {isHighPriority && (
          <span className="w-2 h-2 rounded-full bg-destructive flex-shrink-0" />
        )}
        
        <span className={cn("text-sm flex-shrink-0", !isRead && "font-semibold")}>
          {isBulk ? (
            <Badge className="text-[10px] px-1.5 py-0 bg-amber-100 text-amber-800 border-amber-300">
              Bulk {voNumbers.length}x
            </Badge>
          ) : (
            notification.vo_number ? `VO-${notification.vo_number}` : '–'
          )}
        </span>

        <div className="flex-shrink-0 relative z-10" onClick={handleStatusClick}>
          <StatusChangeDropdown 
            notification={notification} 
            onClick={handleStatusClick}
          />
        </div>

        {!isBulk && isAwaitingStockStatus(notification.status) && (
          <StockStatusBadge
            notificationId={notification.id}
            onClick={() => setShowStockDialog(true)}
          />
        )}

        <span className="ml-auto text-[11px] text-muted-foreground flex-shrink-0">
          {relativeTime}
        </span>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon"
              className="h-6 w-6 flex-shrink-0 -mr-1"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreVertical className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onClick={() => onNotificationClick(notification.id)}>
              <Eye className="h-4 w-4 mr-2" />
              Details
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {isRead ? (
              <DropdownMenuItem onClick={handleMarkAsUnread}>
                <EyeOff className="h-4 w-4 mr-2" />
                Ongelezen
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={handleMarkAsRead}>
                <Eye className="h-4 w-4 mr-2" />
                Gelezen
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            {viewMode === "archived" ? (
              <DropdownMenuItem onClick={(e) => onRestore(e, notification.id)}>
                <ArchiveRestore className="h-4 w-4 mr-2" />
                Herstellen
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={(e) => onArchive(e, notification.id)}>
                <ArchiveIcon className="h-4 w-4 mr-2" />
                Archiveren
              </DropdownMenuItem>
            )}
            <DropdownMenuItem 
              className="text-destructive focus:text-destructive" 
              onClick={(e) => onDelete(e, notification.id)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Verwijderen
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Row 2: Message + Chat indicator */}
      <div className="flex items-center gap-2 mt-0.5">
        <p className={cn(
          "text-xs text-muted-foreground truncate flex-1",
          !isRead && "text-foreground/70"
        )}>
          {notification.message}
        </p>
        <ChatStatusIndicator unreadCount={unreadChatCount} />
      </div>
      
      <StockAlertDialog
        notificationId={notification.id}
        voNumber={notification.vo_number}
        open={showStockDialog}
        onOpenChange={setShowStockDialog}
      />
    </div>
  );
};

export default MobileNotificationCard;
